import React from "react";
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "..";
import { useStyles } from "./DataTable.styles";
import { DataTableProps } from "./DataTable.types";
import DataRow from "./DataRow";

const renderWrapperDefault = (content: React.ReactNode) => content;

function DataTable<T extends { [key: string]: any }>(props: DataTableProps<T>) {
  const {
    columns,
    data,
    idKey,
    enableEdit,
    moreActions,
    onToggleSelect,
    onToggleSelectAll,
    selected = [],
    noHeaders = false,
    renderBodyWrapper = renderWrapperDefault,
    renderHeadWrapper = renderWrapperDefault,
    allSelected,
    allowPan,
    showAllSelected = true,
    selectedDisabled = [],
    sidePadding,
    hideCheckbox = false,
    ...otherProps
  } = props;

  const classes = useStyles(props);

  const toggleAll = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onToggleSelectAll?.(event.target.checked);
    },
    [onToggleSelectAll]
  );

  const isSelected = React.useCallback(
    (id: string) => selected.indexOf(id) !== -1,
    [selected]
  );

  const isDisabled = React.useCallback(
    (id: string) => selectedDisabled.indexOf(id) !== -1,
    [selectedDisabled]
  );

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={0}>
        <TableContainer allowPan={allowPan}>
          <Table
            className={classes.table}
            size="medium"
            sidePadding={sidePadding}
            {...otherProps}
          >
            {!noHeaders && (
              <TableHead>
                <TableRow>
                  {!hideCheckbox && (
                    <TableCell className={classes.checkboxCell}>
                      {showAllSelected && (
                        <Checkbox
                          color="primary"
                          onChange={toggleAll}
                          checked={allSelected}
                          disabled={!enableEdit}
                          aria-label="Select/Deselect all"
                        />
                      )}
                    </TableCell>
                  )}
                  {columns.map((column, index) => (
                    <TableCell
                      key={index}
                      width={column.width}
                      {...column.cellProps}
                    >
                      {renderHeadWrapper(column.header)}
                    </TableCell>
                  ))}
                  {enableEdit && !!moreActions?.length && (
                    <TableCell className={classes.actionsCell} />
                  )}
                </TableRow>
              </TableHead>
            )}

            <TableBody>
              {data.length ? (
                data.map((row: any) => (
                  <DataRow<T>
                    key={row[idKey]}
                    row={row}
                    columns={columns}
                    checked={isSelected(row[idKey] as unknown as string)}
                    renderBodyWrapper={renderBodyWrapper}
                    idKey={idKey}
                    onToggleSelect={onToggleSelect}
                    enableEdit={enableEdit}
                    moreActions={moreActions}
                    sidePadding={sidePadding}
                    checkedDisabled={isDisabled(
                      row[idKey] as unknown as string
                    )}
                    hideCheckbox={hideCheckbox}
                  />
                ))
              ) : (
                <TableRow style={{ height: 53 * 5 }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}

export default React.memo(DataTable) as unknown as typeof DataTable;
